"use strict";
import { Web3Provider as EthersWeb3Provider } from "@ethersproject/providers";
import { CustomUserProperties, WalletConnectionResult } from "@uniswap/analytics-events";
import { UNISWAP_EXTENSION_CONNECTOR_NAME, recentConnectorIdAtom } from "components/Web3Provider/constants";
import { createWeb3Provider } from "components/Web3Provider/createWeb3Provider";
import { wagmiConfig } from "components/Web3Provider/wagmiConfig";
import { RPC_PROVIDERS } from "constants/providers";
import { useAccount } from "hooks/useAccount";
import { useEthersWeb3Provider } from "hooks/useEthersProvider";
import usePrevious from "hooks/usePrevious";
import { useUpdateAtom } from "jotai/utils";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { setUniswapWagmiConfig } from "uniswap/src/features/providers/wagmiBridge";
import { useConnectedWallets } from "state/wallets/hooks";
import { CONVERSION_EVENTS } from "uniswap/src/data/rest/conversionTracking/constants";
import { useConversionTracking } from "uniswap/src/data/rest/conversionTracking/useConversionTracking";
import { useIsSupportedChainId } from "uniswap/src/features/chains/hooks/useSupportedChainId";
import { FeatureFlags } from "uniswap/src/features/gating/flags";
import { useFeatureFlag } from "uniswap/src/features/gating/hooks";
import { setUserProperty } from "uniswap/src/features/telemetry/user";
import { logger } from "utilities/src/logger/logger";
import { getCurrentPageFromLocation } from "utils/urlRoutes";
import { WalletType, getWalletMeta } from "utils/walletMeta";
import { useAccount as useAccountWagmi } from "wagmi";
const Web3Provider = createWeb3Provider({ wagmiConfig });
export default Web3Provider;
export function Web3ProviderUpdater() {
  const account = useAccount();
  const provider = useEthersWeb3Provider();
  const isSupportedChain = useIsSupportedChainId(account.chainId);
  const { connector } = useAccount();
  const { pathname } = useLocation();
  const currentPage = getCurrentPageFromLocation(pathname);
  const networkProvider = isSupportedChain && account.chainId ? RPC_PROVIDERS[account.chainId] : void 0;
  const { trackConversions } = useConversionTracking(account.address);
  const updateRecentConnectorId = useUpdateAtom(recentConnectorIdAtom);
  useEffect(() => {
    setUniswapWagmiConfig(wagmiConfig);
  }, []);
  useEffect(() => {
    if (connector) {
      updateRecentConnectorId(connector.id);
    }
  }, [connector, updateRecentConnectorId]);
  const shouldTrace = useFeatureFlag(FeatureFlags.TraceJsonRpc);
  useEffect(() => {
    if (shouldTrace) {
      provider?.on("debug", trace);
      if (provider !== networkProvider) {
        networkProvider?.on("debug", trace);
      }
    }
    return () => {
      provider?.off("debug", trace);
      networkProvider?.off("debug", trace);
    };
  }, [networkProvider, provider, shouldTrace]);
  const accountWagmiChainId = useAccountWagmi().chainId;
  const previousConnectedChainId = usePrevious(account.isConnected ? accountWagmiChainId : void 0);
  useEffect(() => {
    const chainChanged = previousConnectedChainId && previousConnectedChainId !== accountWagmiChainId;
    if (chainChanged) {
      if (account.address && accountWagmiChainId) {
        setUserProperty(CustomUserProperties.CHAIN_ID, accountWagmiChainId);
        setUserProperty(CustomUserProperties.ALL_WALLET_CHAIN_IDS, accountWagmiChainId, true);
      }
    }
  }, [account.address, accountWagmiChainId, connector?.type, currentPage, previousConnectedChainId]);
  const previousAccount = usePrevious(account.address);
  const [connectedWallets, addConnectedWallet] = useConnectedWallets();
  useEffect(() => {
    if (account.address && account.address !== previousAccount) {
      const walletName = connector?.name ?? "Network";
      const walletType = connector?.type ?? "unknown";
      const peerWalletAgent = provider ? getWalletMeta(provider)?.agent : void 0;
      const isReconnect = connectedWallets.some(
        (wallet) => wallet.account === account.address && wallet.walletName === walletName
      );
      provider?.send("web3_clientVersion", []).then((clientVersion) => {
        setUserProperty(CustomUserProperties.WALLET_VERSION, clientVersion);
      }).catch((error) => {
        logger.warn("Web3Provider", "Updater", "Failed to get client version", error);
      });
      if (accountWagmiChainId) {
        setUserProperty(CustomUserProperties.CHAIN_ID, accountWagmiChainId);
        setUserProperty(CustomUserProperties.ALL_WALLET_CHAIN_IDS, accountWagmiChainId, true);
      }
      setUserProperty(CustomUserProperties.WALLET_ADDRESS, account.address);
      setUserProperty(CustomUserProperties.ALL_WALLET_ADDRESSES_CONNECTED, account.address, true);
      setUserProperty(CustomUserProperties.WALLET_TYPE, walletType);
      const walletConnectedProperties = {
        result: WalletConnectionResult.SUCCEEDED,
        wallet_address: account.address,
        wallet_name: walletName,
        wallet_type: walletType,
        is_reconnect: isReconnect,
        peer_wallet_agent: peerWalletAgent,
        page: currentPage
      };
      if (connector?.name === WalletType.WALLET_CONNECT) {
        connector?.getProvider().then((externalProvider) => {
          const provider2 = externalProvider;
          const meta = getWalletMeta(new EthersWeb3Provider(provider2));
          const name = meta?.name ?? walletName;
          const agent = meta?.agent ?? peerWalletAgent;
          setUserProperty(CustomUserProperties.WALLET_NAME, name);
          setUserProperty(CustomUserProperties.PEER_WALLET_AGENT, agent ?? "");
        }).catch((error) => {
          logger.warn("Web3Provider", "Updater", "Failed to get wallet connect metadata", error);
        });
      } else {
        setUserProperty(CustomUserProperties.WALLET_NAME, walletName);
        setUserProperty(CustomUserProperties.PEER_WALLET_AGENT, peerWalletAgent ?? "");
      }
      if (walletName === UNISWAP_EXTENSION_CONNECTOR_NAME) {
        trackConversions(CONVERSION_EVENTS.Extension.Downloaded);
      }
      trackConversions(CONVERSION_EVENTS.Web.WalletConnected);
      addConnectedWallet({ account: account.address, walletName });
    }
  }, [
    account.address,
    accountWagmiChainId,
    addConnectedWallet,
    connectedWallets,
    connector,
    currentPage,
    previousAccount,
    provider,
    trackConversions
  ]);
  return null;
}
function trace(event) {
  if (!event?.request) {
    return;
  }
  const { method, id, params } = event.request;
  logger.debug("Web3Provider", "provider", "trace", { method, id, params });
}
